/*---------------------------------------------------------------------------------------------
 *  Copyright (c) i-Bridge Systems Philippines Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/
import { Box, Typography, Grid, Tooltip } from '@mui/material';
import { capitalizeWords } from '../../../utilities/capitalizeWords';
import { formatISOToPHT } from '../../../utilities/date';

const style = {
  mr: 2,
  fontWeight: 700,
};

const boxStyle = {
  mx: 0,
  my: 1,
  px: 3,
  py: 2,
};

const boxInfoStyle = {
  bgcolor: '#3A4152',
  width: 1,
  p: 1,
  px: 2,
  mt: 1,
  borderRadius: 1,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
};

const sectionHeaderStyle = {
  color: '#84d8c5',
  fontWeight: 700,
  mb: 1,
};

const detailHeaderStyle = {
  color: '#fff',
  fontWeight: 700,
};

const detailsStyle = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: 1,
  display: 'inline-block',
  cursor: 'default',
  minHeight: 24,
};

export const PersonalInformation = (props) => {
  return (
    <Box sx={{ mb: 3, padding: '12px' }}>
      <Box sx={boxStyle}>
        <Grid container spacing={2} rowSpacing={2.5}>
          <Grid item xs={12}>
            <Typography variant="h5" sx={sectionHeaderStyle}>
              Personal Details
            </Typography>
          </Grid>

          <Grid item md={4}>
            <Typography variant="caption" sx={detailHeaderStyle}>
              First Name
            </Typography>
            <Box sx={boxInfoStyle}>
              <Tooltip title={capitalizeWords(props.empData.firstname)} arrow>
                <Typography
                  variant="body1"
                  sx={{
                    ...detailsStyle,
                    textTransform: 'capitalize',
                  }}
                >
                  {props.empData.firstname}
                </Typography>
              </Tooltip>
            </Box>
          </Grid>

          <Grid item md={4}>
            <Typography variant="caption" sx={detailHeaderStyle}>
              Middle Name
            </Typography>
            <Box sx={boxInfoStyle}>
              <Tooltip title={capitalizeWords(props.empData.middlename)} arrow>
                <Typography
                  variant="body1"
                  sx={{
                    ...detailsStyle,
                    textTransform: 'capitalize',
                  }}
                >
                  {props.empData.middlename}
                </Typography>
              </Tooltip>
            </Box>
          </Grid>

          <Grid item md={4}>
            <Typography variant="caption" sx={detailHeaderStyle}>
              Last Name
            </Typography>
            <Box sx={boxInfoStyle}>
              <Tooltip title={capitalizeWords(props.empData.lastname)} arrow>
                <Typography
                  variant="body1"
                  sx={{
                    ...detailsStyle,
                    textTransform: 'capitalize',
                  }}
                >
                  {props.empData.lastname}
                </Typography>
              </Tooltip>
            </Box>
          </Grid>

          <Grid item md={4}>
            <Typography variant="caption" sx={detailHeaderStyle}>
              Nickname
            </Typography>
            <Box sx={boxInfoStyle}>
              <Tooltip title={capitalizeWords(props.empData.nickname)} arrow>
                <Typography
                  variant="body1"
                  sx={{
                    ...detailsStyle,
                    textTransform: 'capitalize',
                  }}
                >
                  {props.empData.nickname}
                </Typography>
              </Tooltip>
            </Box>
          </Grid>

          <Grid item md={4}>
            <Typography variant="caption" sx={detailHeaderStyle}>
              Place of Birth
            </Typography>
            <Box sx={boxInfoStyle}>
              <Tooltip
                title={capitalizeWords(props.empData.placeofbirth)}
                arrow
              >
                <Typography
                  variant="body1"
                  sx={{
                    ...detailsStyle,
                    textTransform: 'capitalize',
                  }}
                >
                  {props.empData.placeofbirth}
                </Typography>
              </Tooltip>
            </Box>
          </Grid>

          <Grid item md={4}>
            <Typography variant="caption" sx={detailHeaderStyle}>
              Date of Birth
            </Typography>
            <Box sx={boxInfoStyle}>
              <Tooltip title={formatISOToPHT(props.empData?.dateofbirth)} arrow>
                <Typography
                  variant="body1"
                  sx={{
                    ...detailsStyle,
                  }}
                >
                  {formatISOToPHT(props.empData?.dateofbirth)}
                </Typography>
              </Tooltip>
            </Box>
          </Grid>

          <Grid item md={4}>
            <Typography variant="caption" sx={detailHeaderStyle}>
              Age
            </Typography>
            <Box sx={boxInfoStyle}>
              <Tooltip title={props.empData.age} arrow>
                <Typography
                  variant="body1"
                  sx={{
                    ...detailsStyle,
                  }}
                >
                  {props.empData.age}
                </Typography>
              </Tooltip>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h5" sx={{ ...sectionHeaderStyle, mt: 3 }}>
              Contact Details
            </Typography>
          </Grid>

          <Grid item md={6}>
            <Typography variant="caption" sx={detailHeaderStyle}>
              Cellphone No.
            </Typography>
            <Box sx={boxInfoStyle}>
              <Tooltip title={props.empData.cellphone} arrow>
                <Typography
                  variant="body1"
                  sx={{
                    ...detailsStyle,
                  }}
                >
                  {props.empData.cellphone}
                </Typography>
              </Tooltip>
            </Box>
          </Grid>

          <Grid item md={6}>
            <Typography variant="caption" sx={detailHeaderStyle}>
              Email
            </Typography>
            <Box sx={boxInfoStyle}>
              <Tooltip title={props.empData.email} arrow>
                <Typography
                  variant="body1"
                  sx={{
                    ...detailsStyle,
                  }}
                >
                  {props.empData.email}
                </Typography>
              </Tooltip>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h5" sx={{ ...sectionHeaderStyle, mt: 3 }}>
              Physical & Legal Details
            </Typography>
          </Grid>

          <Grid item md={4}>
            <Typography variant="caption" sx={detailHeaderStyle}>
              Sex
            </Typography>
            <Box sx={boxInfoStyle}>
              <Tooltip title={capitalizeWords(props.empData.sex)} arrow>
                <Typography
                  variant="body1"
                  sx={{
                    ...detailsStyle,
                    textTransform: 'capitalize',
                  }}
                >
                  {props.empData.sex}
                </Typography>
              </Tooltip>
            </Box>
          </Grid>

          <Grid item md={4}>
            <Typography variant="caption" sx={detailHeaderStyle}>
              Civil Status
            </Typography>
            <Box sx={boxInfoStyle}>
              <Tooltip title={capitalizeWords(props.empData.civilstatus)} arrow>
                <Typography
                  variant="body1"
                  sx={{
                    ...detailsStyle,
                    textTransform: 'capitalize',
                  }}
                >
                  {props.empData.civilstatus}
                </Typography>
              </Tooltip>
            </Box>
          </Grid>

          <Grid item md={4}>
            <Typography variant="caption" sx={detailHeaderStyle}>
              Citizenship
            </Typography>
            <Box sx={boxInfoStyle}>
              <Tooltip title={capitalizeWords(props.empData.citizenship)} arrow>
                <Typography
                  variant="body1"
                  sx={{
                    ...detailsStyle,
                    textTransform: 'capitalize',
                  }}
                >
                  {props.empData.citizenship}
                </Typography>
              </Tooltip>
            </Box>
          </Grid>

          <Grid item md={4}>
            <Typography variant="caption" sx={detailHeaderStyle}>
              Height
            </Typography>
            <Box sx={boxInfoStyle}>
              <Tooltip
                title={
                  (props.empData.height ?? '')
                    ? `${props.empData.height} cm`
                    : ''
                }
                arrow
              >
                <Typography
                  variant="body1"
                  sx={{
                    ...detailsStyle,
                  }}
                >
                  {(props.empData.height ?? '')
                    ? `${props.empData.height} cm`
                    : ''}
                </Typography>
              </Tooltip>
            </Box>
          </Grid>

          <Grid item md={4}>
            <Typography variant="caption" sx={detailHeaderStyle}>
              Weight
            </Typography>
            <Box sx={boxInfoStyle}>
              <Tooltip
                title={props.empData.weight ? `${props.empData.weight} kg` : ''}
                arrow
              >
                <Typography
                  variant="body1"
                  sx={{
                    ...detailsStyle,
                  }}
                >
                  {props.empData.weight ? `${props.empData.weight} kg` : ''}
                </Typography>
              </Tooltip>
            </Box>
          </Grid>

          <Grid item md={4}>
            <Typography variant="caption" sx={detailHeaderStyle}>
              Blood Type
            </Typography>
            <Box sx={boxInfoStyle}>
              <Tooltip title={capitalizeWords(props.empData.bloodtype)} arrow>
                <Typography
                  variant="body1"
                  sx={{
                    ...detailsStyle,
                    textTransform: 'capitalize',
                  }}
                >
                  {props.empData.bloodtype}
                </Typography>
              </Tooltip>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h5" sx={{ ...sectionHeaderStyle, mt: 3 }}>
              Government Records
            </Typography>
          </Grid>

          <Grid item md={4}>
            <Typography variant="caption" sx={detailHeaderStyle}>
              Passport
            </Typography>
            <Box sx={boxInfoStyle}>
              <Tooltip title={props.empData.passport} arrow>
                <Typography
                  variant="body1"
                  sx={{
                    ...detailsStyle,
                  }}
                >
                  {props.empData.passport}
                </Typography>
              </Tooltip>
            </Box>
          </Grid>

          <Grid item md={4}>
            <Typography variant="caption" sx={detailHeaderStyle}>
              SSS
            </Typography>
            <Box sx={boxInfoStyle}>
              <Tooltip title={props.empData.sss} arrow>
                <Typography
                  variant="body1"
                  sx={{
                    ...detailsStyle,
                  }}
                >
                  {props.empData.sss}
                </Typography>
              </Tooltip>
            </Box>
          </Grid>

          <Grid item md={4}>
            <Typography variant="caption" sx={detailHeaderStyle}>
              Pag-Ibig
            </Typography>
            <Box sx={boxInfoStyle}>
              <Tooltip title={props.empData.pagibig} arrow>
                <Typography
                  variant="body1"
                  sx={{
                    ...detailsStyle,
                  }}
                >
                  {props.empData.pagibig}
                </Typography>
              </Tooltip>
            </Box>
          </Grid>

          <Grid item md={4}>
            <Typography variant="caption" sx={detailHeaderStyle}>
              PhilHealth
            </Typography>
            <Box sx={boxInfoStyle}>
              <Tooltip title={props.empData.philhealth} arrow>
                <Typography
                  variant="body1"
                  sx={{
                    ...detailsStyle,
                  }}
                >
                  {props.empData.philhealth}
                </Typography>
              </Tooltip>
            </Box>
          </Grid>

          <Grid item md={4}>
            <Typography variant="caption" sx={detailHeaderStyle}>
              GSIS
            </Typography>
            <Box sx={boxInfoStyle}>
              <Tooltip title={props.empData.gsis} arrow>
                <Typography
                  variant="body1"
                  sx={{
                    ...detailsStyle,
                  }}
                >
                  {props.empData.gsis}
                </Typography>
              </Tooltip>
            </Box>
          </Grid>

          <Grid item md={4}>
            <Typography variant="caption" sx={detailHeaderStyle}>
              TIN
            </Typography>
            <Box sx={boxInfoStyle}>
              <Tooltip title={props.empData.tin} arrow>
                <Typography
                  variant="body1"
                  sx={{
                    ...detailsStyle,
                  }}
                >
                  {props.empData.tin}
                </Typography>
              </Tooltip>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h5" sx={{ ...sectionHeaderStyle, mt: 3 }}>
              Others
            </Typography>
          </Grid>

          <Grid item md={6}>
            <Typography variant="caption" sx={detailHeaderStyle}>
              Skills
            </Typography>
            <Box sx={boxInfoStyle}>
              <Tooltip title={capitalizeWords(props.empData.skills)} arrow>
                <Typography
                  variant="body1"
                  sx={{
                    ...detailsStyle,
                    textTransform: 'capitalize',
                  }}
                >
                  {props.empData.skills}
                </Typography>
              </Tooltip>
            </Box>
          </Grid>

          <Grid item md={6}>
            <Typography variant="caption" sx={detailHeaderStyle}>
              Hobbies
            </Typography>
            <Box sx={boxInfoStyle}>
              <Tooltip title={capitalizeWords(props.empData.hobbies)} arrow>
                <Typography
                  variant="body1"
                  sx={{
                    ...detailsStyle,
                    textTransform: 'capitalize',
                  }}
                >
                  {props.empData.hobbies}
                </Typography>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};
